import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Building2, Save } from 'lucide-react';
import { toast } from 'sonner';
import { TenantTypeSchema, type TenantSummary, type TenantType } from '@bliks-io/contracts';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Checkbox,
  Label
} from '@bliks-io/ui';

import { useUpdateTenant } from '@/pages/admin/api/mutations/useTenants';
import { TENANT_TYPE_LABEL_KEYS } from './TenantTypeBadge';

interface TenantProvisioningCardProps {
  tenant: TenantSummary;
}

/**
 * Which kinds of workspace this partner tenant may set up for others and then
 * manage (plan 050) — e.g. the sales app of BLIKS IO for demos, trials and
 * customers. Writes the TENANT, so it saves on its own like the seat card.
 */
export function TenantProvisioningCard({ tenant }: TenantProvisioningCardProps) {
  const { t } = useTranslation();
  const updateTenant = useUpdateTenant();
  const stored = tenant.provisioning?.tenantTypes ?? [];
  const [draft, setDraft] = useState<TenantType[] | null>(null);
  const selected = draft ?? stored;

  const hasChanges =
    draft !== null &&
    (draft.length !== stored.length || draft.some((type) => !stored.includes(type)));

  const toggle = (type: TenantType, checked: boolean) =>
    setDraft(
      TenantTypeSchema.options.filter((option) =>
        option === type ? checked : selected.includes(option)
      )
    );

  const handleSave = async () => {
    try {
      await updateTenant.mutateAsync({
        id: tenant._id,
        data: {
          provisioning: selected.length > 0 ? { tenantTypes: selected } : null
        }
      });
      setDraft(null);
      toast.success(t('adminSettings.tenant.saved'));
    } catch {
      toast.error(t('adminSettings.tenant.saveFailed'));
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Building2 className="w-5 h-5 text-muted-foreground" />
          <CardTitle>{t('adminSettings.tenant.provisioningTitle')}</CardTitle>
        </div>
        <CardDescription>{t('adminSettings.tenant.provisioningDescription')}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col gap-4">
          {TenantTypeSchema.options.map((type) => (
            <div key={type} className="flex items-center gap-3">
              <Checkbox
                id={`provisioning-${type}`}
                checked={selected.includes(type)}
                onCheckedChange={(checked) => toggle(type, checked === true)}
              />
              <Label htmlFor={`provisioning-${type}`}>{t(TENANT_TYPE_LABEL_KEYS[type])}</Label>
            </div>
          ))}

          <div className="flex justify-end">
            <Button onClick={handleSave} loading={updateTenant.isPending} disabled={!hasChanges}>
              <Save className="w-4 h-4 mr-2" />
              {t('common.Save')}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
